/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import MsDyn365 from '@msdyn365-commerce/core';
import { ILoyaltyAutoEnrollData } from './loyalty-auto-enroll.data';
import { ILoyaltyAutoEnrollProps } from './loyalty-auto-enroll.props.autogenerated';
import { computed } from 'mobx';
import { issueLoyalty, IssueLoyaltyInput } from '@msdyn365-commerce-modules/retail-actions';
import { Customer } from '@msdyn365-commerce/retail-proxy';
import { updateAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CustomersDataActions.g';
import { initializeKlaviyoLoyaltyProfileAsync } from '../../actions/DataActionExtension.g';
import { IProfileModel } from '../../actions/DataServiceEntities.g';

export interface ILoyaltyAutoEnrollViewProps extends ILoyaltyAutoEnrollProps<ILoyaltyAutoEnrollData> {
    showRedirectModal: boolean;
    errorMessage: string;
    shouldRedirect: boolean;
}

export interface ILoyaltyAutoEnrollState {
    shouldRedirect: boolean;
    errorMessage: string;
}

/**
 *
 * LoyaltyAutoEnroll component
 * @extends {React.PureComponent<ILoyaltyAutoEnrollProps<ILoyaltyAutoEnrollData>, ILoyaltyAutoEnrollState>}
 */
class LoyaltyAutoEnroll extends React.PureComponent<ILoyaltyAutoEnrollProps<ILoyaltyAutoEnrollData>, ILoyaltyAutoEnrollState> {
    private showRedirectModal: boolean = false;
    private avoidRedirect: boolean = false;

    constructor(props: ILoyaltyAutoEnrollProps<ILoyaltyAutoEnrollData>) {
        super(props);
        this.state = { shouldRedirect: false, errorMessage: '' };
    }

    @computed
    private get isAuthenticated(): boolean {
        const {
            context: {
                request: { user }
            }
        } = this.props;
        return user && user.isAuthenticated;
    }

    public componentDidUpdate(
        prevProps: Readonly<ILoyaltyAutoEnrollProps<ILoyaltyAutoEnrollData>>,
        prevState: Readonly<ILoyaltyAutoEnrollState>
    ): void {
        if (this.state.shouldRedirect) {
            this.redirect();
        }
    }

    private redirect() {
        const profilePath = this.props.config.profileUrlPathname ?? '/myprofile';

        if (!MsDyn365.isBrowser) {
            setTimeout(() => {
                this.redirect();
            }, 500);
        }

        if (this.state.shouldRedirect && window.location.pathname !== profilePath) {
            window.location.href = `${window.location.origin}${profilePath}`;
        } else {
            return;
        }
    }

    private async initalizeKlaviyoProfile(loyaltyCard: string | undefined): Promise<void> {
        if (this.props.data.customerInformation.result) {
            const customerEntity = this.props.data.customerInformation.result;

            // Collect data needed for intalization
            const userProfile: IProfileModel = {
                FirstName: customerEntity.FirstName,
                LastName: customerEntity.LastName,
                Email: customerEntity.Email,
                ExternalId: loyaltyCard ?? '',
                LiquorSmsOptIn: false,
                LiquorEmailOptIn: false,
                CannabisSmsOptIn: false,
                CannabisEmailOptIn: false
            };

            return new Promise<void>((resolve, reject) => {
                initializeKlaviyoLoyaltyProfileAsync({ callerContext: this.props.context.actionContext }, userProfile)
                    .then(res => {
                        resolve();
                    })
                    .catch(error => {
                        reject(error);
                    });
            });
        } else {
            // TODO: What to do if customer information doesn't load, immediately throw an error?
            return new Promise<void>((resolve, reject) => {
                reject('Error loading customer information');
            });
        }
    }

    private async updateD365Phone(phoneNumber: string): Promise<void> {
        if (this.props.data.customerInformation.result) {
            const customerEntity: Customer = JSON.parse(JSON.stringify(this.props.data.customerInformation.result));

            customerEntity.Phone = phoneNumber;

            const primaryContact = customerEntity.Contacts?.filter(
                contact => contact && contact.IsPrimary && contact.AddressTypeValue === 1 // 1 = Phone
            )?.[0];

            if (primaryContact) {
                primaryContact.Value = phoneNumber;
            }

            return new Promise<void>((resolve, reject) => {
                updateAsync({ callerContext: this.props.context.actionContext }, customerEntity)
                    .then(res => {
                        resolve();
                    })
                    .catch(error => {
                        reject(error);
                    });
            });
        }
    }

    private async verifyProfiles(loyaltyCard: string | undefined): Promise<void> {
        let newProfileInitalization = false;

        const klaviyoProfile = this.props.data.klaviyoProfile.result;

        // If a Klaviyo profile does not exist, attempt to initialize a new Klaviyo profile using the Loyalty Card number.

        if (!klaviyoProfile) {
            try {
                await this.initalizeKlaviyoProfile(loyaltyCard);
                newProfileInitalization = true;
            } catch (error) {
                this.setState({ errorMessage: 'Error initalizing profile' });
                return;
            }
        }

        const customerInformation = this.props.data.customerInformation.result;
        const userClaims = this.props.context.request.user.userClaims;

        if (customerInformation) {
            if (!customerInformation.Phone && (userClaims?.PhoneNumber || klaviyoProfile?.Phone)) {
                // Save to D365 if no phone number exists
                try {
                    if (klaviyoProfile?.Phone) {
                        await this.updateD365Phone(klaviyoProfile?.Phone.slice(2));
                    } else if (userClaims?.PhoneNumber) {
                        await this.updateD365Phone(userClaims?.PhoneNumber);
                    }
                } catch (error) {
                    this.setState({ errorMessage: 'Error updating Phone Number' });
                    return;
                }
            } else if (
                customerInformation.Phone &&
                userClaims?.PhoneNumber &&
                customerInformation.Phone !== userClaims.PhoneNumber &&
                !klaviyoProfile?.Phone
            ) {
                // Overwrite D365 phone number if different from userClaims
                //TODO: What if Klaviyo fails to load? Then D365 will be updated to original userClaims.
                try {
                    await this.updateD365Phone(userClaims.PhoneNumber);
                } catch (error) {
                    this.setState({ errorMessage: 'Error updating Phone Number' });
                    return;
                }
            } else if (customerInformation.Phone && klaviyoProfile?.Phone && customerInformation.Phone !== klaviyoProfile.Phone.slice(2)) {
                // this.setState({ shouldRedirect: true });
                try {
                    await this.updateD365Phone(klaviyoProfile.Phone.slice(2));
                } catch (error) {
                    this.setState({ errorMessage: 'Error updating Phone Number' });
                    return;
                }
                return;
            }
        } else {
            this.setState({ errorMessage: 'Failed to load customer information' });
            return;
        }

        if (newProfileInitalization) {
            this.setState({ shouldRedirect: true });
            return;
        }

        if (klaviyoProfile) {
            // TODO: Check with SNDL
            if (
                klaviyoProfile.Phone === undefined ||
                klaviyoProfile.CannabisSmsOptIn === undefined ||
                klaviyoProfile.CannabisEmailOptIn === undefined ||
                klaviyoProfile.LiquorSmsOptIn === undefined ||
                klaviyoProfile.LiquorEmailOptIn === undefined
            ) {
                this.setState({ shouldRedirect: true });
                return;
            }
        }
    }

    private checkAllowedUrls(): void {
        const requestUrl = this.props.context.request.url.requestUrl.toString();
        const allowedUrls = this.props.config.allowedUrls?.map(x => x.destinationUrl);

        if (allowedUrls !== undefined && allowedUrls.length > 0) {
            this.avoidRedirect = allowedUrls.some(x => requestUrl.includes(x));
        }
    }

    public render(): JSX.Element | null {
        this.checkAllowedUrls();

        if (this.avoidRedirect) {
            return null;
        }

        const {
            data: { loyaltyCard },
            context: {
                request: { apiSettings, user },
                actionContext
            }
        } = this.props;

        if (!this.state.errorMessage && !this.state.shouldRedirect && !this.showRedirectModal) {
            // PII redirect cases
            if (this.isAuthenticated && !loyaltyCard?.result?.CardNumber) {
                // If authenticated but no loyalty card, issue loyalty card
                this.showRedirectModal = true;
                issueLoyalty(new IssueLoyaltyInput(apiSettings), actionContext)
                    .then(res => {
                        this.verifyProfiles(res.CardNumber)
                            .then(res => console.log(res))
                            .catch(error => console.log(error));
                    })
                    .catch(err => {
                        this.setState({ errorMessage: 'Failed to issue loyalty card' });
                    });
            } else if (this.isAuthenticated && loyaltyCard?.result?.CardNumber && user.emailAddress) {
                // Loyalty card but PII not filled
                if (
                    !this.props.data.customerInformation.result?.Phone ||
                    !this.props.data.klaviyoProfile.result ||
                    !this.props.data.klaviyoProfile.result.Phone ||
                    this.props.data.customerInformation.result.Phone !== this.props.data.klaviyoProfile.result.Phone.slice(2)
                ) {
                    this.showRedirectModal = true;
                    this.verifyProfiles(this.props.data.loyaltyCard?.result?.CardNumber ?? undefined)
                        .then(res => console.log(res))
                        .catch(error => console.log(error));
                }
            }
        }

        // TODO: Should there be a close button? won't they be able to freely navigate then?
        const IViewProps: ILoyaltyAutoEnrollViewProps = {
            ...this.props,
            showRedirectModal: this.showRedirectModal,
            errorMessage: this.state.errorMessage,
            shouldRedirect: this.state.shouldRedirect
        };
        return this.props.renderView(IViewProps);
    }
}

export default LoyaltyAutoEnroll;
